import { aaveV4Oracles, aaveV4SpokeLenderKey } from '@1delta/data-sdk'
import { AaveV4OracleAbi } from '@1delta/abis'
import {
  AaveV4QueryMeta,
  FetcherResult,
  MulticallCall,
  OraclePriceEntry,
  ParseContext,
} from '../types'
import { createMarketUid } from '../../../utils'

/**
 * Groups oracle entries by oracle address since entries sharing the same
 * oracle can be batched into one getReservesPrices call. Flat by chain
 * (no fork dimension) — the spoke address on each entry is the join key
 * between the price and the lending data.
 */
function getAaveV4OracleGroups(chainId: string) {
  const oracleEntries = aaveV4Oracles()?.[chainId] ?? []

  const byOracle = new Map<
    string,
    {
      decimals: number
      entries: { underlying: string; reserveId: number; spoke: string }[]
    }
  >()

  for (const entry of oracleEntries) {
    if (!entry?.oracle || entry.oracle === '0x') continue
    if (!entry.spoke) continue

    const key = entry.oracle.toLowerCase()
    let group = byOracle.get(key)
    if (!group) {
      group = { decimals: entry.decimals ?? 8, entries: [] }
      byOracle.set(key, group)
    }

    group.entries.push({
      underlying: entry.underlying.toLowerCase(),
      reserveId: entry.reserveId,
      spoke: entry.spoke.toLowerCase(),
    })
  }

  const groups: {
    oracle: string
    decimals: number
    entries: { underlying: string; reserveId: number; spoke: string }[]
  }[] = []

  for (const [oracle, group] of byOracle) {
    groups.push({ oracle, decimals: group.decimals, entries: group.entries })
  }

  return groups
}

/**
 * Build multicall calls for Aave V4 oracle prices.
 *
 * Per oracle group:
 *   [0..N-1] oracle.getReservePrice(reserveId) — one per entry
 *
 * Decimals come from metadata, no on-chain call needed.
 * Underlying addresses come from metadata, no getReserve call needed.
 */
export function getAaveV4Calls(
  chainId: string,
): FetcherResult<AaveV4QueryMeta>[] {
  const groups = getAaveV4OracleGroups(chainId)
  const results: FetcherResult<AaveV4QueryMeta>[] = []

  for (const { oracle, decimals, entries } of groups) {
    const calls: MulticallCall[] = entries.map((e) => ({
      address: oracle,
      name: 'getReservePrice',
      params: [e.reserveId],
    }))

    results.push({
      calls,
      meta: {
        oracle,
        oracleDecimals: decimals,
        entries,
      },
      lender: 'AAVE_V4',
    })
  }

  return results
}

/**
 * Parse Aave V4 oracle results into OraclePriceEntry[].
 *
 * Layout per tracker:
 *   [0..N-1] = getReservePrice results (one per entry)
 */
export function parseAaveV4Results(
  data: any[],
  meta: AaveV4QueryMeta,
  context: ParseContext,
): OraclePriceEntry[] {
  const { chainId } = context
  const entries: OraclePriceEntry[] = []
  if (data.length === 0) return entries

  const divisor = 10 ** meta.oracleDecimals

  for (let i = 0; i < meta.entries.length; i++) {
    const rawPrice = data[i]
    if (rawPrice === undefined || rawPrice === '0x') continue

    const entry = meta.entries[i]

    try {
      const priceNum =
        Number(BigInt(rawPrice.toString())) / divisor
      if (isNaN(priceNum) || priceNum === 0) continue

      // Each spoke is its own lender key. The marketUid the lending
      // normalizer emits is scoped to the per-spoke lender key with
      // the **reserveId** as the leaf — mirror that here so price
      // entries join with their lending entries on the same key.
      const spokeLenderKey = aaveV4SpokeLenderKey(entry.spoke)

      entries.push({
        asset: entry.underlying,
        price: priceNum,
        priceUSD: priceNum,
        marketUid: createMarketUid(
          chainId,
          spokeLenderKey,
          String(entry.reserveId),
        ),
        // Route into the per-spoke lender bucket so price entries live under
        // the same lender key as the lending data (mirrors Morpho's pattern).
        targetLender: spokeLenderKey,
        configId: entry.spoke,
      })
    } catch {
      continue
    }
  }

  return entries
}

/**
 * ABI for Aave V4 oracle price fetching
 */
export function getAaveV4Abi() {
  return AaveV4OracleAbi as any
}

/**
 * Aave V4 Oracle Fetcher module
 */
export const aaveV4Fetcher = {
  getCalls: getAaveV4Calls,
  parse: parseAaveV4Results,
  getAbi: getAaveV4Abi,
}
